import { useQuery } from '@tanstack/react-query';

import type { CrmObject } from '@/types/crm';

import type { HighlightDefinition } from '../registry/entityTypeRegistry';
import { fetchAggregate, readAggregationNumber } from './aggregate';
import type { HighlightResolverResult } from './types';

/**
 * Resolve the entity type to count from an association type name, relative to the
 * record we are viewing.
 *
 * Association type names are directional in this codebase: a company→contact link is
 * spelled `ahoy_company_to_contact` on the company page and `ahoy_contact_to_company`
 * on the contact page. Both ends appear as `_to_` segments, so we pick the side that is
 * NOT the current object's entity type — that is the entity we want to count.
 *
 * The tail segment may be either fully-qualified (`org_*` for custom entities, or
 * mixed associations like `ahoy_company_to_org_pipeline`) or abbreviated (the legacy
 * `ahoy_<a>_to_<b>` convention where the tail drops its `ahoy_` prefix). We only
 * restore the implicit `ahoy_` prefix when the tail isn't already prefixed.
 *
 * Falls back to the leading segment when the object's entity type matches neither
 * side (misconfiguration) or both sides (a self-association like
 * `ahoy_contact_to_contact`).
 */
function resolveTail(tailName: string | undefined, head: string): string {
  if (tailName == null) {
    return head;
  }
  if (tailName.startsWith('ahoy_') || tailName.startsWith('org_')) {
    return tailName;
  }
  return `ahoy_${tailName}`;
}

function parseCountedEntityType(
  associationType: string,
  objectEntityType: string
): string {
  const [head, tailName] = associationType.split('_to_');
  const tail = resolveTail(tailName, head);
  if (objectEntityType === tail) {
    return head;
  }
  if (objectEntityType === head) {
    return tail;
  }
  return head;
}

/**
 * Resolve the count of entities associated to `object` via `definition.associationType`.
 *
 * Query shape: POST /aggregate/ with entity_type_name = the entity on the other end of
 * the association from `object`, and a filter selecting rows whose association to
 * object.entity_id matches.
 *
 * Value is always a string (even '0') — association_count never renders empty.
 */
export function useHighlightAssociationCount(
  orgId: number,
  object: CrmObject,
  definition: Extract<HighlightDefinition, { kind: 'association_count' }>
): HighlightResolverResult {
  const entityId = object.entity_id;
  const { associationType } = definition;
  const countedEntityType = parseCountedEntityType(
    associationType,
    object.entity_type_name
  );

  const { data, isLoading, isError } = useQuery({
    queryKey: [
      'highlight',
      'association_count',
      orgId,
      entityId,
      associationType,
    ],
    queryFn: () =>
      fetchAggregate(orgId, {
        entity_type_name: countedEntityType,
        aggregations: [{ function: 'count', alias: 'count' }],
        filter_groups: [
          {
            filters: [
              {
                association: associationType,
                operator: 'exact',
                value: entityId,
              },
            ],
          },
        ],
      }),
    staleTime: 30_000,
    enabled: !!orgId && !!entityId,
  });

  const count = data ? readAggregationNumber(data.results, 'count') : null;

  return {
    value: count !== null ? String(count) : null,
    isLoading,
    isError,
  };
}
